import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import perfType from './prop-types';
import { attrs2month, month2Label } from './utils';
import { sinceYears, yearsAndMonthsAgo, formatPercent } from '../shared-utils';

const Container = styled.table`
  width: 100%;

  &&&& {
    margin-bottom: 32px;
  }

  & td {
    width: 50%;
    height: 56px;
  }

  & td:first-child, & th:first-child {
    text-align: left;
  }

  & td:last-child, & th:last-child {
    text-align: right;
  }

  & > thead {
    font: ${props => props.theme.fontWeightDccc} 14px/16px ${props => props.theme.fontFamilyBody};
    letter-spacing: 0.1px;
    color: ${props => props.theme.secondaryColor};
    text-transform: uppercase;

    tr {
      border-bottom: 1px solid ${props => props.theme.secondaryColor};

      /* stylelint-disable-next-line no-descending-specificity */
      th {
        padding-top: 0px;
        padding-bottom: 16px;
      }
    }
  }

  & > tbody {
    font: ${props => props.theme.fontWeightRegular} 19px/100% ${props => props.theme.fontFamilyBody};
    letter-spacing: 0.1px;
    color: ${props => props.theme.bodyColor};


    > tr {
      border-bottom: 1px solid rgba(16, 58, 107, 0.07);

      > .all-text {
          height: 30px;
          padding: 8px 0;
          display: table-cell !important;
          text-align: center !important;
          font: ${props => props.theme.fontWeightRegular} 16px/112% ${props => props.theme.fontFamilyBody};
          color: ${props => props.theme.secondaryColor};
      }
    }

    /* stylelint-disable-next-line no-descending-specificity */
    td {
      padding: 20px 0;
    }

    td:first-child {
      display: flex;
      flex-direction: column;
      white-space: nowrap;
      small {
        margin-left: 6px;
        font-size: 16px;
      }

      .date {
        color: ${props => props.theme.primaryColor};
        font-weight: ${props => props.theme.fontWeightBold};
        white-space: nowrap;
      }
    }

    > tr:first-child > td {
      padding-top: 32px;
    }

    > tr:last-child {
      border-bottom: 1px solid #d7dfe7;

      > td {
        padding-bottom: 48px;
      }
    }
  }
`;

const Return = ({ value, months }) => {
  if (Number.isNaN(value)) {
    return null;
  }

  return (
    <tr>
      <td>
        <span>
          {month2Label(months)}
          {months > 12 && <small>p.a.</small>}
        </span>
      </td>
      <td>{formatPercent(value)}</td>
    </tr>
  );
};

Return.propTypes = {
  value: PropTypes.number.isRequired,
  months: PropTypes.number.isRequired,
};

const ReturnSinceInception = ({ value, since }) => (
  <tr>
    <td>
      <span>
        Since Inception
        <small>p.a.</small>
      </span>
      <span className="date">{sinceYears(since, true)}</span>
    </td>
    <td>{formatPercent(value)}</td>
  </tr>
);

ReturnSinceInception.propTypes = {
  value: PropTypes.number.isRequired,
  since: PropTypes.string.isRequired,
};

const ReturnPartial = ({ since }) => (
  <tr>
    <td colSpan={2} className="all-text">
      <span>{`Began ${yearsAndMonthsAgo(since)}`}</span>
    </td>
  </tr>
);

ReturnPartial.propTypes = {
  since: PropTypes.string.isRequired,
};

const getSlot = (list, months) => _.find(list, { months }) || { months, value: Number.NaN };
const isPartial = list => _.find(list, { value: Number.NaN });

export default function Returns({ perf, since }) {
  return (
    <Container>
      <thead>
        <tr>
          <th>period</th>
          <th>return</th>
        </tr>
      </thead>
      <tbody>
        {_.values(attrs2month).map(d => <Return {...getSlot(perf, d)} key={d} />)}
        {!!isPartial(perf) && <ReturnPartial since={since} />}
        <ReturnSinceInception {...getSlot(perf, undefined)} since={since} />
      </tbody>
    </Container>
  );
}

Returns.propTypes = {
  perf: perfType.isRequired,
  since: PropTypes.string.isRequired,
};
